import React, { CSSProperties, FC, useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { useTranslation, Trans } from 'react-i18next'
import { RupeeCircleIcon } from '@okcredit/web-lib'
import { THEME } from 'components/theme'
import { newPaths } from 'routes'
import { formatCurrency } from 'utils/formatCurrency'
import {
  getPurchasedSubscriptions,
  listOfferedSubscriptionPlans
} from 'services/Api/SubscriptionApi'
import SubscriptionBadge from 'assets/icons/subscription/subscription-badge.svg'
import PaymentModal from '../MyPlan/PaymentModal'
import { usePlanCheckout, SUBSCRIPTION_UPDATED_EVENT } from '../MyPlan/usePlanCheckout'
import { trackPremiumBannerShown } from '../MyPlan/subscriptionEvents'

// Days-remaining threshold below which the "expires soon" banner shows. Mirrors
// Android's `subscription_expiry_prompt_days` Firebase Remote Config (default 7).
const EXPIRY_PROMPT_DAYS = 7
const DAY_MS = 24 * 60 * 60 * 1000

// Floor (truncate) to match Android's `TimeUnit.MILLISECONDS.toDays(expiryTime - now)`.
const daysLeft = (expirySec: number | null): number | null =>
  expirySec ? Math.floor((expirySec * 1000 - Date.now()) / DAY_MS) : null

// Inline styles: this component mounts above the Header at first paint; inline keeps
// the layout independent of stylesheet injection order.
const styles: Record<string, CSSProperties> = {
  banner: {
    order: -1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '10px 20px',
    backgroundColor: THEME.brandTintOrange,
    borderBottom: `1px solid ${THEME.brandTintOrangeBorder}`,
    flexWrap: 'wrap'
  },
  left: { display: 'flex', alignItems: 'center', gap: 12 },
  iconCircle: {
    flexShrink: 0,
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: THEME.brandDarkOrange,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  textWrap: { display: 'flex', flexDirection: 'column' },
  title: { fontSize: 14, fontWeight: 'bold', color: THEME.brandBlack },
  subtitle: { fontSize: 12, color: THEME.brandDarkerGrey },
  actions: { display: 'flex', alignItems: 'center', gap: 10 },
  changeBtn: {
    cursor: 'pointer',
    border: `1px solid ${THEME.brandGreyPrimary}`,
    backgroundColor: THEME.brandWhite,
    color: THEME.brandBlack,
    borderRadius: 20,
    padding: '7px 16px',
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },
  payBtn: {
    cursor: 'pointer',
    border: 'none',
    backgroundColor: THEME.brandGreen,
    color: THEME.onBrand,
    borderRadius: 20,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },
  strikeAmount: {
    textDecoration: 'line-through',
    opacity: 0.7,
    fontWeight: 400,
    marginRight: 3
  }
}

interface ActiveSub {
  expirySec: number | null
  amountPaise: number | null
  discountPaise: number
  planId: string | null
  planName: string
}

const ExpiryBanner: FC = () => {
  const { t } = useTranslation()
  const history = useHistory()

  const [sub, setSub] = useState<ActiveSub | null>(null)
  const isMountedRef = useRef(true)

  // Always fetch the authoritative subscription (the cached expiry can be stale across
  // account/env switches). The banner mounts once with the persistent dashboard layout,
  // so this is a single call per session.
  const loadSub = useCallback(() => {
    // Fetch the offered plans alongside the active sub so the renew amount reflects the
    // current offer (payable = amount − discount), exactly like the My Plan page. Using
    // active_sub.amount alone ignores the discount and would over-charge on renew.
    Promise.all([getPurchasedSubscriptions(), listOfferedSubscriptionPlans()])
      .then(([purchasedRes, plansRes]) => {
        if (!isMountedRef.current) {
          return
        }
        const active = purchasedRes.data.active_sub
        const offeredPlan = (plansRes.data.subscription_plans || []).find(
          (p) => p.id === active?.plan_id
        )
        setSub({
          expirySec: active?.expiry_time ?? null,
          amountPaise: offeredPlan?.amount ?? active?.amount ?? null,
          discountPaise: offeredPlan?.discount ?? 0,
          planId: active?.plan_id ?? null,
          planName: active?.name ?? ''
        })
      })
      .catch(() => {
        if (isMountedRef.current) {
          setSub({
            expirySec: null,
            amountPaise: null,
            discountPaise: 0,
            planId: null,
            planName: ''
          })
        }
      })
  }, [])

  useEffect(() => {
    isMountedRef.current = true
    loadSub()
    return () => {
      isMountedRef.current = false
    }
  }, [loadSub])

  // Refresh when a payment succeeds anywhere (banner or My Plan page) so the banner
  // hides/updates immediately after renewal.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }
    window.addEventListener(SUBSCRIPTION_UPDATED_EVENT, loadSub)
    return () => window.removeEventListener(SUBSCRIPTION_UPDATED_EVENT, loadSub)
  }, [loadSub])

  // Renewing extends the plan, so refresh the subscription when the popup closes after
  // a successful payment (which usually hides the banner).
  const checkout = usePlanCheckout(loadSub, 'expiry_banner')

  const days = daysLeft(sub?.expirySec ?? null)
  const shouldShow = days !== null && days >= 0 && days <= EXPIRY_PROMPT_DAYS

  // Banner impression — once per mount when it first becomes visible (Android parity).
  const bannerTrackedRef = useRef(false)
  useEffect(() => {
    if (shouldShow && !bannerTrackedRef.current) {
      bannerTrackedRef.current = true
      trackPremiumBannerShown('expiry_banner', sub?.planName || '')
    }
  }, [shouldShow, sub])

  const goToPlan = () => history.push(newPaths.myPlan)
  // "Pay to Renew" opens the payment popup in place for the current plan.
  // Payable after the offer/discount (paise) — what we both show and charge.
  const renewPayablePaise =
    sub?.amountPaise !== null && sub?.amountPaise !== undefined
      ? sub.amountPaise - sub.discountPaise
      : null

  const onRenew = () => {
    if (sub?.planId && sub.amountPaise !== null) {
      checkout.startPayment({
        id: sub.planId,
        amount: sub.amountPaise,
        discount: sub.discountPaise,
        name: sub.planName
      })
    } else {
      goToPlan()
    }
  }

  return (
    <>
      {shouldShow ? (
        <div style={styles.banner}>
          <div style={styles.left}>
            <div style={styles.iconCircle}>
              <img src={SubscriptionBadge} width={20} height={20} alt="" />
            </div>
            <div style={styles.textWrap}>
              <span style={styles.title}>{t('dashboard:SubscriptionExpiresSoon')}</span>
              <span style={styles.subtitle}>{t('dashboard:RenewUninterrupted')}</span>
            </div>
          </div>
          <div style={styles.actions}>
            <button style={styles.changeBtn} onClick={goToPlan}>
              <RupeeCircleIcon size={16} fill={THEME.brandBlack} />
              {t('dashboard:ChangePlan')}
            </button>
            <button style={styles.payBtn} onClick={onRenew}>
              {renewPayablePaise ? (
                sub && sub.discountPaise > 0 && sub.amountPaise ? (
                  <Trans
                    i18nKey="dashboard:PayToRenewOffer"
                    values={{
                      original: formatCurrency(Math.round(sub.amountPaise / 100)),
                      amount: formatCurrency(Math.round(renewPayablePaise / 100))
                    }}
                    components={[<span key="o" style={styles.strikeAmount} />]}
                  />
                ) : (
                  t('dashboard:PayToRenew', {
                    amount: formatCurrency(Math.round(renewPayablePaise / 100))
                  })
                )
              ) : (
                t('dashboard:RenewPlan')
              )}
            </button>
          </div>
        </div>
      ) : null}
      {checkout.payOpen ? (
        <PaymentModal
          loading={checkout.payLoading}
          error={checkout.payError}
          qrCodeUrl={checkout.qrCodeUrl}
          payableAmount={checkout.payableRupees}
          planName={checkout.planName}
          isComplete={checkout.isComplete}
          isFailed={checkout.isFailed}
          onClose={checkout.closePayment}
          translate={t}
        />
      ) : null}
    </>
  )
}

export default ExpiryBanner
